import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import ComplementaryItem from '@components/balance/complementary-item/ComplementaryItem'
import Page from '@components/Page/Page'
import { batchUpdateProgramComplementaryItems } from '@services/membership/ProgramComplementaryItemService'
import { batchUpdateProgramCurrencies } from '@services/membership/ProgramCurrencyService'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getProgramComplementaryItems,
  selectProgramComplementaryItems,
  setProgramComplementaryItemQuery,
} from '@store/membership/ProgramComplementaryItemReducer'
import {
  getProgramCurrencies,
  selectProgramCurrencies,
  setProgramCurrencyQuery,
} from '@store/membership/ProgramCurrencyReducer'
import { selectSelectedProgram } from '@store/membership/ProgramReducer'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { Permission } from '@utils/permissions'
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  message,
  Row,
  Space,
  Typography,
} from 'antd'
import { useEffect, useState } from 'react'
import { CurrencyInput } from '../CurrencyInput'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from './../../../../App'

const { Title } = Typography

const BalanceAndCreditsTab = () => {
  const [balanceForm] = Form.useForm()
  const [creditForm] = Form.useForm()
  const [isBalanceSaving, setIsBalanceSaving] = useState(false)
  const [isCreditSaving, setIsCreditSaving] = useState(false)
  const { isOpen } = useSBRM()
  const dispatch = useAppDispatch()
  const program = useAppSelector(selectSelectedProgram())
  const programCurrencies = useAppSelector(selectProgramCurrencies())
  const programComplementaryItems = useAppSelector(
    selectProgramComplementaryItems()
  )

  const fetchProgramDetails = () => {
    if (!program) return

    const query: TableParams = {
      ...initialQuery,
      columnKey: 'name',
      order: 'ascend',
      filters: { programs: [program.id] },
    }

    dispatch(setProgramCurrencyQuery(query))
    dispatch(getProgramCurrencies(query))
    dispatch(setProgramComplementaryItemQuery(query))
    dispatch(getProgramComplementaryItems(query))
  }

  useEffect(() => {
    if (!program || isOpen) return

    fetchProgramDetails()
  }, [program, isOpen])

  if (!program) {
    return <></>
  }

  return (
    <Page permissions={Permission.manageProgramsMembership}>
      <Row gutter={[32, 32]}>
        <Col span={12}>
          <Form
            form={balanceForm}
            layout="vertical"
            name="balance"
            onFinish={async (values: any) => {
              const amounts = values.amount
              if (!amounts) return

              const items = programCurrencies
                .filter((item) => amounts[item.id] !== undefined)
                .map((item) => ({ id: item.id, amount: amounts[item.id] }))

              if (!items.length) return

              setIsBalanceSaving(true)
              try {
                await batchUpdateProgramCurrencies(program.slug, items)
                fetchProgramDetails()
                message.success(DEFAULT_SUCCESS)
              } catch {
                message.error(DEFAULT_ERROR)
              } finally {
                setIsBalanceSaving(false)
              }
            }}
          >
            <Flex vertical className="w-full" gap={8}>
              <Space className="w-full space-between">
                <Title level={5} className="m-0">
                  Balance granted upon activation
                </Title>
                <Space>
                  <Button
                    style={{ border: 0 }}
                    type="primary"
                    htmlType="submit"
                    loading={isBalanceSaving}
                    disabled={isBalanceSaving}
                  >
                    Save
                  </Button>
                </Space>
              </Space>

              <Card>
                {programCurrencies.map((item) => (
                  <Flex key={item.id} align="flex-end" gap={8}>
                    <div style={{ flex: 1 }}>
                      <CurrencyInput entity="program" id={item.id} />
                    </div>
                    <SBRM.Actions
                      actions={[SBRMAction.Delete]}
                      entity={Entity.membershipProgramCurrency}
                      entityId={item.id}
                      trigger={
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ marginBottom: 24 }}
                        />
                      }
                    />
                  </Flex>
                ))}
                <SBRM.Actions
                  actions={[SBRMAction.Create]}
                  entity={Entity.membershipProgramCurrency}
                  metadata={[{ key: MetadataKey.Program, value: program.id }]}
                  trigger={
                    <Button type="dashed" block icon={<PlusOutlined />}>
                      Add new balance
                    </Button>
                  }
                />
              </Card>
            </Flex>
          </Form>
        </Col>
        <Col span={12}>
          <Form
            form={creditForm}
            layout="vertical"
            name="credit"
            onFinish={async (values: any) => {
              const quantities = values.quantity
              if (!quantities) return

              const items = programComplementaryItems
                .filter((item) => quantities[item.id] !== undefined)
                .map((item) => ({
                  id: item.id,
                  quantity: quantities[item.id],
                }))

              if (!items.length) return

              setIsCreditSaving(true)
              try {
                await batchUpdateProgramComplementaryItems(program.slug, items)
                fetchProgramDetails()
                message.success(DEFAULT_SUCCESS)
              } catch {
                message.error(DEFAULT_ERROR)
              } finally {
                setIsCreditSaving(false)
              }
            }}
          >
            <Flex vertical className="w-full" gap={8}>
              <Space className="w-full space-between">
                <Title level={5} className="m-0">
                  Credits granted upon activation
                </Title>
                <Space>
                  <Button
                    style={{ border: 0 }}
                    type="primary"
                    htmlType="submit"
                    loading={isCreditSaving}
                    disabled={isCreditSaving}
                  >
                    Save
                  </Button>
                </Space>
              </Space>

              <Card>
                {programComplementaryItems.map((item) => (
                  <Flex key={item.id} align="flex-end" gap={8}>
                    <div style={{ flex: 1 }}>
                      <ComplementaryItem.Spinner
                        entity="program"
                        id={item.id}
                      />
                    </div>
                    <SBRM.Actions
                      actions={[SBRMAction.Delete]}
                      entity={Entity.programComplementaryItem}
                      entityId={item.id}
                      trigger={
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ marginBottom: 24 }}
                        />
                      }
                    />
                  </Flex>
                ))}
                <SBRM.Actions
                  actions={[SBRMAction.Create]}
                  entity={Entity.complementaryItem}
                  metadata={[
                    {
                      key: MetadataKey.Program,
                      value: program?.id,
                    },
                  ]}
                  trigger={
                    <Button type="dashed" block icon={<PlusOutlined />}>
                      Create new item
                    </Button>
                  }
                />
              </Card>
            </Flex>
          </Form>
        </Col>
      </Row>
    </Page>
  )
}

export default BalanceAndCreditsTab
